import React, { useEffect, useMemo, useRef, useState } from 'react';

// Глобальная шина, чтобы останавливать другие плееры
const bus = typeof window !== 'undefined'
    ? (window.__VOICE_BUS__ ||= new EventTarget())
    : null;

// Вспомогательный экспорт — можно вызвать при закрытии модалки, если нужно
export function stopAllVoices() {
    bus?.dispatchEvent(new CustomEvent('voice:stopAll'));
}

export default function VoicePlayer({ src, filename, durationHintSec = 0, onTranscribe }) {
    const audioRef = useRef(null);
    const barRef = useRef(null);

    const [dur, setDur] = useState(Number(durationHintSec) || 0);
    const [pos, setPos] = useState(0);
    const [playing, setPlaying] = useState(false);

    const name = useMemo(
        () => filename || (src || '').split('/').pop() || 'voice',
        [src, filename]
    );

    const fmt = (t = 0) => {
        const s = Math.max(0, Math.floor(t));
        const mm = String(Math.floor(s / 60)).padStart(2, '0');
        const ss = String(s % 60).padStart(2, '0');
        return `${mm}:${ss}`;
    };

    // сброс при смене файла
    useEffect(() => {
        setPos(0);
        setPlaying(false);
        setDur((d) => d || Number(durationHintSec) || 0);
    }, [src, durationHintSec]);

    // ошибки загрузки
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onErr = () => {
            console.warn('[VoicePlayer] audio error', {
                code: el?.error?.code,
                message: el?.error?.message,
                networkState: el?.networkState,
                readyState: el?.readyState,
                src: el?.currentSrc || src,
            });
        };
        el.addEventListener('error', onErr);
        return () => el.removeEventListener('error', onErr);
    }, [src]);

    // берём реальную длительность из метаданных (если сервер её не дал)
    const onLoadedMeta = () => {
        const d = Number(audioRef.current?.duration);
        const final = Number.isFinite(d) && d > 0 ? d : (Number(durationHintSec) || 0);
        setDur(final);
    };

    const onTimeUpdate = () => {
        const t = Number(audioRef.current?.currentTime);
        if (Number.isFinite(t)) setPos(t);
    };

    const onEnded = () => {
        setPlaying(false);
        setPos(dur);
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    // автопауза остальных + сброс позиции у предыдущего
    useEffect(() => {
        if (!bus) return;
        const handlerPlay = (e) => {
            const me = audioRef.current;
            if (!me) return;
            if (e.detail?.src !== src && !me.paused) {
                me.pause();
                me.currentTime = 0;
                setPos(0);
                setPlaying(false);
            }
        };
        const handlerStopAll = () => {
            const me = audioRef.current;
            if (!me) return;
            me.pause();
            me.currentTime = 0;
            setPos(0);
            setPlaying(false);
        };
        bus.addEventListener('voice:play', handlerPlay);
        bus.addEventListener('voice:stopAll', handlerStopAll);
        return () => {
            bus.removeEventListener('voice:play', handlerPlay);
            bus.removeEventListener('voice:stopAll', handlerStopAll);
        };
    }, [src]);

    const canPlayType = () => {
        const a = document.createElement('audio');
        const u = (src || '').toLowerCase();
        if (u.endsWith('.webm')) return !!a.canPlayType('audio/webm; codecs="opus"');
        if (u.endsWith('.ogg') || u.endsWith('.oga')) return !!a.canPlayType('audio/ogg; codecs="opus"');
        if (u.endsWith('.mp3')) return !!a.canPlayType('audio/mpeg');
        if (u.endsWith('.m4a') || u.endsWith('.aac')) return !!a.canPlayType('audio/mp4');
        if (u.endsWith('.wav')) return !!a.canPlayType('audio/wav');
        return !!a.canPlayType('audio/*');
    };

    const toggle = async () => {
        const el = audioRef.current;
        if (!el) return;
        if (!canPlayType()) return;
        try {
            if (el.paused) {
                bus?.dispatchEvent(new CustomEvent('voice:play', { detail: { src } }));
                await el.play();
                setPlaying(true);
            } else {
                el.pause();
                setPlaying(false);
            }
        } catch (e) {
            if (String(e?.name) !== 'AbortError') console.warn('[VoicePlayer] play() failed', e);
        }
    };

    const onBarClick = (e) => {
        const el = audioRef.current;
        const bar = barRef.current;
        if (!el || !bar) return;

        const rect = bar.getBoundingClientRect();
        if (rect.width <= 0 || !Number.isFinite(dur) || dur <= 0) return;

        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const ratio = x / rect.width;
        const t = ratio * dur;

        if (Number.isFinite(t)) {
            el.currentTime = t;
            setPos(t);
        }
    };

    const pct = dur > 0 ? Math.min(100, Math.max(0, (pos / dur) * 100)) : 0;

    const u = (src || '').toLowerCase();
    const isWebm = u.endsWith('.webm');
    const isOgg = u.endsWith('.ogg') || u.endsWith('.oga');
    const isMp3 = u.endsWith('.mp3');
    const isM4a = u.endsWith('.m4a') || u.endsWith('.aac');
    const isWav = u.endsWith('.wav');

    return (
        <div className="vplayer">
            <audio
                ref={audioRef}
                preload="metadata"
                onLoadedMetadata={onLoadedMeta}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                onPlay={onPlay}
                onPause={onPause}
            >
                {isWebm && <source src={src} type="audio/webm" />}
                {isOgg && <source src={src} type="audio/ogg" />}
                {isMp3 && <source src={src} type="audio/mpeg" />}
                {isM4a && <source src={src} type="audio/mp4" />}
                {isWav && <source src={src} type="audio/wav" />}
                {!isWebm && !isOgg && !isMp3 && !isM4a && !isWav && <source src={src} />}
            </audio>

            <button className="vplayer__btn" onClick={toggle} title={playing ? 'Пауза' : 'Воспроизвести'}>
                {playing ? '⏸' : '▶️'}
            </button>

            <div className="vplayer__center">
                <div className="vplayer__bar" ref={barRef} onClick={onBarClick}>
                    <div className="vplayer__bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="vplayer__meta">
                    <span className="vplayer__time">{fmt(pos)} / {fmt(dur)}</span>
                    <span className="vplayer__name" title={name}>{name}</span>
                </div>
            </div>

            {onTranscribe && (
                <button className="vplayer__tr" onClick={() => onTranscribe({ href: src, fileName: filename })} title="Расшифровать">
                    📝
                </button>
            )}
        </div>
    );
}
